import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Sparkles, ArrowRight, Loader2, Film, Image as ImageIcon, FileText, Music } from "lucide-react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";

const kindIcon = (k) => ({ video: Film, image: ImageIcon, audio: Music, document: FileText }[k] || FileText);
const SUGGESTIONS = [
  "Drone shots from last week",
  "Behind the scenes interviews",
  "Logo and brand assets",
  "Voiceovers in english",
];

export const AISearchDialog = ({ open, onOpenChange, initialQuery = "" }) => {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open && initialQuery) {
      setQ(initialQuery);
      setTimeout(() => submit(initialQuery), 100);
    }
    if (!open) {
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpenChange]);

  const submit = async (query) => {
    const text = (query ?? q).trim();
    if (!text) return;
    setQ(text); setLoading(true); setResult(null);
    try {
      const res = await api.post("/search", { query: text });
      setResult(res.data);
    } catch (e) {
      setResult({ summary: "Search failed. Try again.", matches: [] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl p-0 overflow-hidden border-black/10" data-testid="ai-search-dialog">
        <DialogTitle className="sr-only">AI search</DialogTitle>
        <DialogDescription className="sr-only">Search your media library in plain english</DialogDescription>
        <div className="border-b border-black/10 p-4 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-[#E60000]" strokeWidth={1.5} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Ask in plain english… 'sunset clips with people, last month'"
            className="flex-1 bg-transparent outline-none text-base font-medium tracking-tight placeholder:text-black/35"
            data-testid="ai-search-input"
          />
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-black/50" /> :
            <button onClick={() => submit()} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000]" data-testid="ai-search-submit">
              Search
            </button>
          }
        </div>

        {!result && !loading && (
          <div className="p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/40">Try</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => submit(s)}
                  data-testid={`suggestion-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-sm border border-black/15 px-3 py-1.5 rounded-sm hover:border-black hover:bg-black hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {result && (
          <div className="p-5 space-y-4 max-h-[60vh] overflow-auto" data-testid="ai-search-results">
            {result.locked && (
              <div className="border border-[#E60000]/30 bg-[#E60000]/5 p-3 text-xs uppercase tracking-[0.2em] font-bold text-[#E60000]">
                Locked · upgrade your squad to unlock natural-language AI search
              </div>
            )}
            {result.summary && (
              <div className="border-l-2 border-[#E60000] pl-3 text-sm leading-relaxed">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E60000] mb-1">Claude Sonnet 4.5</div>
                {result.summary}
              </div>
            )}
            <div className="space-y-1">
              {result.matches?.length === 0 && <div className="text-sm text-black/50">No matches in your library.</div>}
              {result.matches?.map((m) => {
                const Icon = kindIcon(m.media_kind);
                return (
                  <button
                    key={m.id}
                    onClick={() => { onOpenChange(false); nav(`/media/${m.id}`); }}
                    data-testid={`search-result-${m.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 border border-transparent hover:border-black/15 hover:bg-black/5 group transition-colors text-left"
                  >
                    <div className="w-9 h-9 bg-black text-white rounded-sm flex items-center justify-center">
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold tracking-tight truncate">{m.title}</div>
                      <div className="text-xs text-black/50 truncate">{m.original_filename}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
